/* eslint-disable @typescript-eslint/no-unused-vars */
import { IBaseCrudService } from './base-crud-service';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CustomError } from '../../../../helpers/error/custom-error';
import { BaseModel } from '../../../../models/common/base-model/base-model';
import { ResponsePagination } from '../../../../models/common/response/response-pagination/response-pagination';
import { ILogService } from '../log/log-service';
import { IAuthService } from '../../auth/auth-service';
import { IdStatus } from 'src/models/common/base/id';

export class BaseCrudServiceImpl<
  T extends BaseModel,
> implements IBaseCrudService<T> {
  constructor(
    protected apiPath: string,
    protected jsonDataToFn: (jsonData: any) => T,
    public logService: ILogService,
    public authService: IAuthService,
  ) {}

  async get(id: string): Promise<T> {
    try {
      await this.logService.info('BaseCrudServiceImpl.get -> start');

      const result = await this.authService.http.get(`${this.apiPath}/${id}`);

      // Verifica se o retorno é uma paginação ou o objeto direto
      if (
        ResponsePagination.isResponsePagination(result) &&
        result['items']?.[0]
      ) {
        return this.jsonDataToFn(result['items'][0]);
      }

      return this.jsonDataToFn(result);
    } catch (error) {
      throw CustomError.handleError('BaseCrudServiceImpl.get -> error', error);
    } finally {
      await this.logService.info('BaseCrudServiceImpl.get -> end');
    }
  }

  async create(data: T): Promise<string> {
    try {
      await this.logService.info('BaseCrudServiceImpl.create -> start');

      const result = await this.authService.http.post(`${this.apiPath}`, data);

      return result?.id;
    } catch (error) {
      throw CustomError.handleError(
        'BaseCrudServiceImpl.create -> error',
        error,
      );
    } finally {
      await this.logService.info('BaseCrudServiceImpl.create -> end');
    }
  }

  async update(id: string, data: T): Promise<void> {
    try {
      await this.logService.info('BaseCrudServiceImpl.update -> start');

      await this.authService.http.patch(`${this.apiPath}/${id}`, data);
    } catch (error) {
      throw CustomError.handleError(
        'BaseCrudServiceImpl.update -> error',
        error,
      );
    } finally {
      await this.logService.info('BaseCrudServiceImpl.update -> end');
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await this.logService.info('BaseCrudServiceImpl.delete -> start');

      await this.authService.http.delete(`${this.apiPath}/${id}`);
    } catch (error) {
      throw CustomError.handleError(
        'BaseCrudServiceImpl.delete -> error',
        error,
      );
    } finally {
      await this.logService.info('BaseCrudServiceImpl.delete -> end');
    }
  }

  async search(
    keyword: string,
    page: number,
    limit: number,
    all?: boolean,
    status?: IdStatus,
    direction?: 'asc' | 'desc',
  ): Promise<ResponsePagination> {
    try {
      await this.logService.info('BaseCrudServiceImpl.search -> start');

      const result = await this.authService.http.post(
        `${this.apiPath}/search`,
        {
          query: { keyword },
          page: page || 1,
          limit: limit || 10,
          all: all || false,
          status: status || undefined,
          direction: direction || 'asc',
        },
      );

      return ResponsePagination.fromJson(result);
    } catch (error) {
      throw CustomError.handleError(
        'BaseCrudServiceImpl.search -> error',
        error,
      );
    } finally {
      await this.logService.info('BaseCrudServiceImpl.search -> end');
    }
  }

  async inactivate(id: string): Promise<void> {
    try {
      await this.logService.info('BaseCrudServiceImpl.inactivate -> start');

      await this.authService.http.patch(`${this.apiPath}/${id}/inactive`, {});
    } catch (error) {
      throw CustomError.handleError(
        'BaseCrudServiceImpl.inactivate -> error',
        error,
      );
    } finally {
      await this.logService.info('BaseCrudServiceImpl.inactivate -> end');
    }
  }

  async activate(id: string): Promise<void> {
    try {
      await this.logService.info('BaseCrudServiceImpl.activate -> start');

      await this.authService.http.patch(`${this.apiPath}/${id}/active`, {});
    } catch (error) {
      throw CustomError.handleError(
        'BaseCrudServiceImpl.activate -> error',
        error,
      );
    } finally {
      await this.logService.info('BaseCrudServiceImpl.activate -> end');
    }
  }
}
